import classNames from 'classnames'
import get from 'lodash-es/get'
import React, { Fragment, FunctionComponent, ReactElement } from 'react'
import DateTimeHeader from '@/app/_components/DateTimeHeader/DateTimeHeader'
import HeadWithSubhead from '@/app/_components/HeadWithSubhead/HeadWithSubhead'
import LineItems from '@/app/_components/LineItems/LineItems'
import LocationWithAddress from '@/app/_components/LocationWithAddress/LocationWithAddress'
import { sortAddOns } from '@/utils/components/LineItemsSection.utils'
import { getOrderSummarySelectedDate } from '@/utils/dates.utils'
import { createEventMetaObject, handleVenueMeta } from '@/utils/events.utils'
import TXT from '@/messages'
import { EVENT_CATEGORIES } from '@/config/events.config'
// styles
import orderSummaryStyles from '../OrderSummary/OrderSummary.module.css'
import { OrderSummaryRenderContext } from '@/types/order-summary.types'

type Props = {
  cart: Transformed.Cart
  meta?: readonly Transformed.MetaData[]
  events?: readonly Transformed.Event[]
  venues?: readonly Transformed.Venue[]
  addOns: LocalLineItemWithMeta[]
  admissionTickets: LocalLineItemWithMeta[]
  eventTickets: LocalLineItemWithMeta[]
  displayDelete?: boolean
  handleDeleteTickets?: ({ tickets, lineItems }: DeleteArgs) => Promise<void>
  adjustedQtyForTier: (args: QtyArgs) => number
  renderContext: OrderSummaryRenderContext
}

const LineItemsSection: FunctionComponent<Props> = ({
  meta = undefined,
  events = undefined,
  venues = undefined,
  cart,
  admissionTickets,
  addOns,
  eventTickets,
  displayDelete = false,
  handleDeleteTickets = undefined,
  adjustedQtyForTier,
  renderContext,
}) => {
  const {
    admissionType,
    displayMembership,
    sitePath,
    timezone,
  } = renderContext
  const hasAdmissionTickets = admissionTickets.length > 0
  const hasAddOns = addOns.length > 0
  const hasEventTickets = eventTickets.length > 0
  const displayLineItems = ({
    lineItems,
    type,
  }: {
    lineItems: LocalLineItemWithMeta[]
    type: Transformed.EventCategory[],
  }): Array<ReactElement<HTMLElement>> => lineItems.map((lineItem: LocalLineItemWithMeta) => (
    <Fragment key={`${lineItem.category.id}${lineItem.items[0].timeslot?.id}`}>
      <LineItems
        cart={cart}
        type={type}
        key={`${lineItem.category.id}${lineItem.items[0].timeslot?.id}`}
        lineItemWithMeta={lineItem}
        adjustedQtyForTier={adjustedQtyForTier}
        showDeleteButton={displayDelete}
        handleDelete={handleDeleteTickets}
      />
      <hr className="separator dark" />
    </Fragment>
  ))

  return (
    <>
      {/** Tickets */}
      {cart
        && hasAdmissionTickets
        && displayLineItems({
          lineItems: admissionTickets || [],
          type: ['Admission'],
        })}
      {/** Add-Ons  */}
      {hasAddOns
        && displayLineItems({
          lineItems: addOns || [],
          type: ['AddOn'],
        })}
      {/** Event Tickets */}
      {cart && hasEventTickets && events && (
        <>
          {[...events]
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((event) => {
              const foundMeta = meta?.filter((metaItem) => metaItem.resourceId === event.id)
              // TODO this is a good candidate for the server side
              const eventMeta = createEventMetaObject(foundMeta || [])
              const { venueAddress, venueTitle } = handleVenueMeta(eventMeta, venues?.[0] as Transformed.Venue)
              const filtered = eventTickets.filter((li) => li.event.id === event.id)
              const eventStartTime = get(filtered, '[0].items.[0].timeslot.startTime', null)
              const eventEndTime = get(filtered, '[0].items.[0].timeslot.endTime', null)
              const date = getOrderSummarySelectedDate(eventStartTime, timezone)
              return (
                <Fragment key={event.id}>
                  <HeadWithSubhead
                    head={event.title}
                    link={`${sitePath}/${event.slug}`}
                    subhead={eventMeta.supportingActs}
                  />
                  <DateTimeHeader
                    admissionType={admissionType}
                    displayMembership={displayMembership}
                    entryTime={eventStartTime}
                    endTime={eventEndTime}
                    date={date}
                    timezone={timezone}
                  />
                  <LocationWithAddress title={venueTitle} address={venueAddress} />
                  <h1 className={classNames(
                    orderSummaryStyles.GeneralTitle,
                    orderSummaryStyles.MinorTitle,
                  )}
                  >
                    {TXT.lineItemsSection.items}
                  </h1>
                  {displayLineItems({
                    lineItems: sortAddOns(filtered),
                    type: EVENT_CATEGORIES,
                  })}
                </Fragment>
              )
            })}
        </>
      )}
    </>
  )
}

export default LineItemsSection
